/**
 * SignNdfDocument — NDF Document Signing Page
 *
 * A refined, professional document signing experience for NDF-format documents.
 * Uses the NdfRenderer component for block-based content with integrated
 * signature fields.
 *
 * Features:
 * - Token validation flow
 * - NdfRenderer for document content + signature fields
 * - Multi-signer support (filters blocks by signerId)
 * - SignerAcknowledgment modal before signing
 * - Signature progress tracking
 * - Decline flow with reason
 * - Success confirmation
 *
 * Aesthetic: "Legal Trust" — warm stone tones, subtle paper texture,
 * smooth motion, authoritative yet approachable.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle2,
  X,
  AlertTriangle,
  Send,
  FileText,
  PenLine,
} from 'lucide-react';
import { Button, Card, CardContent, Spinner } from '@respectly/ui';
import { executeRecaptcha } from '@respectly/utils';
import type { NexusDocument } from '@respectly/shared-types';
import { getSignatureBlocks } from '@respectly/shared-types';
import { PortalLayout } from '../layouts/PortalLayout';
import { NdfRenderer } from '../components/NdfRenderer';
import { SignerAcknowledgment } from '../components/SignerAcknowledgment';
import { ContactSupportModal } from '../components/ContactSupportModal';
import { MobileSigningBar } from '../components/MobileSigningBar';
import { NdfSigningDetails } from '../components/NdfSigningDetails';
import { NdfMobileSigningDrawer } from '../components/NdfMobileSigningDrawer';
import { ExpiredPage } from './ExpiredPage';
import { CompletedPage } from './CompletedPage';
import { NotFoundPage } from './NotFoundPage';
import { LoadingPage } from './LoadingPage';

// ─── Types ──────────────────────────────────────────────

interface SignatureData {
  type: 'draw' | 'type' | 'upload';
  data: string;
  fontFamily?: string;
}

interface SignerContext {
  role: string;
  order: number;
  totalSigners: number;
}

interface DocumentData {
  signatureId: string;
  status: string;
  document: {
    id: string;
    name: string;
    fileUrl?: string;
    pageCount?: number;
  };
  contact: {
    firstName: string;
    lastName?: string;
    email?: string;
    phone?: string;
    company?: string;
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
  orgName?: string | null;
  orgBookingLink?: string | null;
  expiresAt?: string;
  signerContext?: SignerContext;
  ndfDocument: NexusDocument | null;
  /** Prior signers' baked signatures (multi-signer NDF) — shown read-only in place. */
  lockedSignatures?: Record<string, { imageDataUrl: string; caption?: string }>;
  /** Prior signers' entered field values (multi-signer NDF) — shown read-only in place. */
  lockedFieldValues?: Record<string, unknown>;
}

// ─── Constants ──────────────────────────────────────────

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// ─── Animation Variants ─────────────────────────────────

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

// ═══════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════

export function SignNdfDocument() {
  // ─── Core State ─────────────────────────────────────
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tokenStatus, setTokenStatus] = useState<string | null>(null);
  const [documentData, setDocumentData] = useState<DocumentData | null>(null);

  // ─── Field Values State ─────────────────────────────
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // ─── Decline State ──────────────────────────────────
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [isDeclined, setIsDeclined] = useState(false);

  // ─── Acknowledgment State ───────────────────────────
  const [showAcknowledgment, setShowAcknowledgment] = useState(true);
  const [hasAcknowledged, setHasAcknowledged] = useState(false);
  const [showContactSupport, setShowContactSupport] = useState(false);

  // ─── Mobile Drawer State (only used on <lg viewports) ──
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // ─── URL Params ─────────────────────────────────────
  const getUrlParams = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const pathParts = window.location.pathname.split('/');
    // Route: /sign-ndf/:signatureId
    const signatureId = pathParts[pathParts.indexOf('sign-ndf') + 1];
    const token = params.get('token') || signatureId;
    return { signatureId, token };
  }, []);

  // ─── Fetch Document ─────────────────────────────────
  useEffect(() => {
    const fetchDocument = async () => {
      const { signatureId, token } = getUrlParams();

      if (!signatureId) {
        setTokenStatus('not_found');
        setIsLoading(false);
        return;
      }

      try {
        // Validate token first
        const recaptchaToken = await executeRecaptcha('ACCESS_CODE');
        const validateResponse = await fetch(
          `${API_BASE}/public/sign/${signatureId}/validate?token=${token}&recaptchaToken=${encodeURIComponent(recaptchaToken)}`
        );
        const validateResult = await validateResponse.json();

        if (!validateResponse.ok || !validateResult.success) {
          setError(validateResult.error?.message || 'Failed to validate token');
          setIsLoading(false);
          return;
        }

        const status = validateResult.data?.status;
        if (status !== 'valid') {
          setTokenStatus(status);
          setIsLoading(false);
          return;
        }

        // Fetch document details — include the signer's local timezone so the
        // server can render date variables ({{date}}, {{today}}, etc.) correctly.
        const signerTz = encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone);
        const docResponse = await fetch(
          `${API_BASE}/public/sign/${signatureId}?token=${token}&tz=${signerTz}`
        );
        const docResult = await docResponse.json();

        if (!docResponse.ok || !docResult.success) {
          if (docResponse.status === 404) {
            setTokenStatus('not_found');
          } else if (docResponse.status === 410) {
            setTokenStatus(docResult.error?.code?.toLowerCase() || 'expired');
          } else {
            setError(docResult.error?.message || 'Failed to load document');
          }
          setIsLoading(false);
          return;
        }

        // Check if this is an NDF document
        if (!docResult.data?.ndfDocument) {
          // Redirect to legacy signing page
          window.location.href = `/sign/${signatureId}?token=${token}`;
          return;
        }

        setDocumentData(docResult.data);
        setTokenStatus('valid');
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching document:', err);
        setError('Failed to connect to server. Please try again.');
        setIsLoading(false);
      }
    };

    fetchDocument();
  }, [getUrlParams]);

  // ─── Signature Blocks (filtered by signer) ──────────
  const signatureBlocks = useMemo(() => {
    if (!documentData?.ndfDocument) return [];

    const allSignatureBlocks = getSignatureBlocks(documentData.ndfDocument);

    // If multi-signer, filter to current signer's fields
    const signerContext = documentData.signerContext;
    if (signerContext) {
      // Find signer ID for this position in signing order
      const signers = documentData.ndfDocument.signers || [];
      const currentSigner = signers.find(
        (s) => s.order === signerContext.order
      );

      if (currentSigner) {
        return allSignatureBlocks.filter((block) => {
          if ('signerId' in block) {
            return block.signerId === currentSigner.id;
          }
          return true; // Include blocks without signerId
        });
      }
    }

    return allSignatureBlocks;
  }, [documentData]);

  // ─── Signature fields the CURRENT signer may sign ──
  // (signatureBlocks is already filtered to this signer for multi-signer documents).
  // Other parties' signature fields are locked/awaiting in the renderer.
  const signableSignatureFieldIds = useMemo(
    () =>
      new Set(
        signatureBlocks
          .filter((block) => block.type === 'field_signature')
          .map((block) => block.id)
      ),
    [signatureBlocks]
  );

  // ─── Required Signature Count ───────────────────────
  const requiredSignatureCount = useMemo(() => {
    return signatureBlocks.filter((block) => {
      if (block.type === 'field_signature') {
        return block.required;
      }
      return true; // PDF overlay signatures are always required
    }).length;
  }, [signatureBlocks]);

  // ─── Signed Count ───────────────────────────────────
  const signedCount = useMemo(() => {
    return signatureBlocks.filter((block) => {
      const value = values[block.id];
      if (block.type === 'field_signature') {
        return value && (value as { data?: string })?.data;
      }
      return !!value;
    }).length;
  }, [signatureBlocks, values]);

  // ─── Progress Percentage ────────────────────────────
  const progressPercent = useMemo(() => {
    if (requiredSignatureCount === 0) return 100;
    return Math.round((signedCount / requiredSignatureCount) * 100);
  }, [signedCount, requiredSignatureCount]);

  // ─── All Required Signed ────────────────────────────
  const allRequiredSigned = useMemo(() => {
    if (requiredSignatureCount === 0) return true;
    return signedCount >= requiredSignatureCount;
  }, [signedCount, requiredSignatureCount]);

  // ─── Field Status List (shared by sidebar + mobile drawer) ──
  const detailFields = useMemo(() => {
    return signatureBlocks.map((block) => {
      const value = values[block.id];
      const isSigned =
        block.type === 'field_signature'
          ? !!(value as { data?: string })?.data
          : !!value;
      const label =
        block.type === 'field_signature' ? block.label : 'Signature';
      return { id: block.id, label, isSigned };
    });
  }, [signatureBlocks, values]);

  // ─── Contact Variable Substitution ──────────────────
  const contactVariables = useMemo((): Record<string, string> => {
    if (!documentData?.contact) return {};
    const c = documentData.contact;
    const firstName = c.firstName || '';
    const lastName = c.lastName || '';
    const now = new Date();
    return {
      // Contact fields
      first_name: firstName,
      last_name: lastName,
      full_name: `${firstName} ${lastName}`.trim(),
      email: c.email || '',
      phone: c.phone || '',
      company: c.company || '',
      // Address fields
      address: c.address || '',
      city: c.city || '',
      state: c.state || '',
      zip: c.zipCode || '',
      country: c.country || '',
      // VariableInserter company_* aliases
      company_address: c.address || '',
      company_city: c.city || '',
      company_state: c.state || '',
      company_zip: c.zipCode || '',
      // Organization fields
      'org.name': documentData.orgName || '',
      'org.companyName': documentData.orgName || '',
      org_name: documentData.orgName || '',
      'org.bookingLink': documentData.orgBookingLink || '',
      // Date fields — use the browser's local timezone with a long date format
      // so that {{date}} renders as "April 30, 2026" in the signer's local date,
      // matching the format produced by the server's resolveFieldValue helper.
      date: now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
      today: now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
      year: now.toLocaleDateString('en-US', { year: 'numeric' }),
      month: now.toLocaleDateString('en-US', { month: 'long' }),
      day: now.toLocaleDateString('en-US', { day: 'numeric' }),
    };
  }, [documentData]);

  // ─── Handle Value Change ────────────────────────────
  const handleChange = useCallback((fieldId: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [fieldId]: value }));
  }, []);

  // ─── Submit Signature ───────────────────────────────
  const handleSubmit = async () => {
    if (!documentData || !allRequiredSigned) return;

    const { signatureId, token } = getUrlParams();
    setIsSubmitting(true);

    try {
      // Collect signature data from values
      const signedFields = signatureBlocks
        .filter((block) => values[block.id])
        .map((block) => ({
          fieldId: block.id,
          signatureData: values[block.id] as SignatureData,
        }));

      // Collect every NON-signature field the signer filled (text/date/time/select/file),
      // keyed by NDF block id. These get baked inline into the signed PDF by the worker —
      // so they land in the right place, just like signatures. Signature blocks go in
      // signedFields; everything else (and only fields with a value) goes here.
      const signatureFieldIds = new Set(signatureBlocks.map((b) => b.id));
      const fieldValues: Record<string, unknown> = {};
      for (const [fieldId, value] of Object.entries(values)) {
        if (signatureFieldIds.has(fieldId)) continue;
        if (value === undefined || value === null || value === '') continue;
        fieldValues[fieldId] = value;
      }

      // Get primary signature for backward compatibility
      const primarySignature = signedFields[0]?.signatureData;

      const response = await fetch(
        `${API_BASE}/public/sign/${signatureId}?token=${token}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            signatureData: primarySignature,
            signedFields,
            fieldValues,
            // Include signer's timezone for audit trail and date variable resolution
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        setError(result.error?.message || 'Failed to submit signature');
        setIsSubmitting(false);
        return;
      }

      setIsSuccess(true);
      setIsSubmitting(false);
    } catch (err) {
      console.error('Error submitting signature:', err);
      setError('Failed to submit signature. Please try again.');
      setIsSubmitting(false);
    }
  };

  // ─── Handle Decline ─────────────────────────────────
  const handleDecline = async () => {
    if (!declineReason.trim()) return;

    const { signatureId, token } = getUrlParams();
    setIsSubmitting(true);

    try {
      const response = await fetch(
        `${API_BASE}/public/sign/${signatureId}/decline?token=${token}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: declineReason }),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        setError(result.error?.message || 'Failed to decline');
        setIsSubmitting(false);
        return;
      }

      setIsDeclined(true);
      setShowDeclineModal(false);
      setIsSubmitting(false);
    } catch (err) {
      console.error('Error declining:', err);
      setError('Failed to decline. Please try again.');
      setIsSubmitting(false);
    }
  };

  // ─── Acknowledgment Handlers ────────────────────────
  const handleAcknowledge = () => {
    setHasAcknowledged(true);
    setShowAcknowledgment(false);
  };

  const handleNotMe = () => {
    setShowAcknowledgment(false);
    setShowContactSupport(true);
  };

  const handleContactSupportClose = () => {
    setShowContactSupport(false);
    setShowAcknowledgment(true);
  };

  // ─── Helper: Signer Full Name ───────────────────────
  const getSignerFullName = () => {
    if (!documentData) return '';
    const { firstName, lastName } = documentData.contact;
    return `${firstName}${lastName ? ' ' + lastName : ''}`;
  };

  // ═══════════════════════════════════════════════════════
  // RENDER STATES
  // ═══════════════════════════════════════════════════════

  if (isLoading) {
    return <LoadingPage message="Preparing your document..." />;
  }

  if (tokenStatus === 'not_found') {
    return <NotFoundPage type="document" />;
  }

  if (tokenStatus === 'expired') {
    return <ExpiredPage type="document" />;
  }

  if (tokenStatus === 'already_completed' || isDeclined) {
    return (
      <CompletedPage
        type={isDeclined ? 'declined' : 'signed'}
        documentName={documentData?.document?.name}
      />
    );
  }

  if (error) {
    return (
      <PortalLayout>
        <div className="flex flex-1 items-center justify-center p-4">
          <Card className="w-full max-w-md border-stone-200 shadow-lg">
            <CardContent className="p-8 text-center">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-red-50"
              >
                <AlertTriangle className="h-10 w-10 text-red-500" />
              </motion.div>
              <h1 className="text-xl font-semibold text-stone-900">
                Something went wrong
              </h1>
              <p className="mt-3 text-stone-600">{error}</p>
              <Button
                variant="outline"
                className="mt-6 border-stone-300"
                onClick={() => window.location.reload()}
              >
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      </PortalLayout>
    );
  }

  if (!documentData || !documentData.ndfDocument) {
    return <NotFoundPage type="document" />;
  }

  // ─── Success State ──────────────────────────────────
  if (isSuccess) {
    return (
      <PortalLayout>
        <div className="flex flex-1 items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="w-full max-w-md"
          >
            <Card className="overflow-hidden border-stone-200 shadow-xl">
              {/* Success gradient header */}
              <div className="relative h-2 bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-500" />

              <CardContent className="p-8 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50"
                >
                  <CheckCircle2 className="h-10 w-10 text-emerald-600" />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <h1 className="text-2xl font-semibold text-stone-900">
                    Document Signed
                  </h1>

                  <p className="mt-3 text-stone-600">
                    Thank you, {documentData.contact.firstName}. Your signature
                    has been applied successfully.
                  </p>

                  {documentData.contact.email && (
                    <p className="mt-2 text-sm text-stone-500">
                      A confirmation will be sent to{' '}
                      <span className="font-medium text-stone-700">
                        {documentData.contact.email}
                      </span>
                    </p>
                  )}
                </motion.div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="mt-8"
                >
                  <Button
                    variant="outline"
                    className="w-full border-stone-300 hover:bg-stone-50"
                    onClick={() => { window.close(); setTimeout(() => { if (!window.closed) window.location.href = '/'; }, 300); }}
                  >
                    Close this page
                  </Button>
                </motion.div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </PortalLayout>
    );
  }

  // ═══════════════════════════════════════════════════════
  // MAIN SIGNING VIEW
  // ═══════════════════════════════════════════════════════

  const ndfDoc = documentData.ndfDocument;

  return (
    <PortalLayout showFooter={false}>
      <div className="flex h-[calc(100vh-3.5rem)] flex-col lg:flex-row">
        {/* ─── Document Content ─────────────────────────── */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="flex-1 overflow-y-auto bg-gradient-to-b from-stone-50 to-white"
        >
          <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
            {/* Document Header */}
            <motion.div variants={itemVariants} className="mb-8">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-stone-100">
                  <FileText className="h-6 w-6 text-stone-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <h1 className="text-xl font-semibold text-stone-900 sm:text-2xl">
                    {ndfDoc.meta.title}
                  </h1>
                  {ndfDoc.meta.description && (
                    <p className="mt-1 text-sm text-stone-500">
                      {ndfDoc.meta.description}
                    </p>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Document Body - NDF Renderer */}
            <motion.div
              variants={itemVariants}
              className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm sm:p-8"
              style={{
                // Subtle paper texture feel
                backgroundImage: `
                  linear-gradient(to bottom, rgba(255,255,255,0.9), rgba(255,255,255,1)),
                  url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")
                `,
                backgroundBlendMode: 'normal',
              }}
            >
              <NdfRenderer
                document={ndfDoc}
                values={values}
                onChange={handleChange}
                readOnly={false}
                variables={contactVariables}
                lockedSignatures={documentData.lockedSignatures}
                lockedFieldValues={documentData.lockedFieldValues}
                signableSignatureFieldIds={signableSignatureFieldIds}
              />
            </motion.div>
          </div>
        </motion.div>

        {/* ─── Sidebar (desktop only) ───────────────────── */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="hidden w-full border-stone-200 bg-white lg:block lg:w-80 lg:border-l"
        >
          <div className="flex h-full flex-col">
            {/* Sidebar Header */}
            <div className="border-b border-stone-100 p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50">
                  <PenLine className="h-5 w-5 text-primary-600" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-stone-900">
                    Sign Document
                  </h2>
                  <p className="text-xs text-stone-500">
                    Review and add your signature
                  </p>
                </div>
              </div>
            </div>

            {/* Sidebar Content */}
            <div className="flex-1 overflow-y-auto p-4">
              <NdfSigningDetails
                signerName={getSignerFullName()}
                email={documentData.contact.email}
                signerContext={documentData.signerContext}
                signedCount={signedCount}
                requiredCount={requiredSignatureCount}
                progressPercent={progressPercent}
                allRequiredSigned={allRequiredSigned}
                fields={detailFields}
                expiresAt={documentData.expiresAt}
              />
            </div>

            {/* Action Buttons */}
            <div className="border-t border-stone-100 p-4">
              <div className="space-y-2">
                <Button
                  variant="default"
                  className="w-full"
                  disabled={!allRequiredSigned || isSubmitting}
                  onClick={handleSubmit}
                >
                  {isSubmitting ? (
                    <Spinner size="sm" className="mr-2" />
                  ) : (
                    <Send className="mr-2 h-4 w-4" />
                  )}
                  Complete Signing
                </Button>

                <Button
                  variant="ghost"
                  className="w-full text-stone-500 hover:text-red-600"
                  onClick={() => setShowDeclineModal(true)}
                  disabled={isSubmitting}
                >
                  <X className="mr-2 h-4 w-4" />
                  Decline to Sign
                </Button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ─── Mobile Action Bar (hidden on desktop) ────── */}
        <div className="order-last lg:hidden">
          <MobileSigningBar
            signedCount={signedCount}
            requiredCount={requiredSignatureCount}
            canSubmit={allRequiredSigned}
            isSubmitting={isSubmitting}
            onComplete={handleSubmit}
            onDecline={() => setShowDeclineModal(true)}
            onOpenDetails={() => setMobileDrawerOpen(true)}
          />
        </div>
      </div>

      {/* ─── Mobile Details Drawer (hidden on desktop) ──── */}
      <NdfMobileSigningDrawer
        open={mobileDrawerOpen}
        onOpenChange={setMobileDrawerOpen}
        documentName={ndfDoc.meta.title}
        signerName={getSignerFullName()}
        email={documentData.contact.email}
        signerContext={documentData.signerContext}
        signedCount={signedCount}
        requiredCount={requiredSignatureCount}
        progressPercent={progressPercent}
        allRequiredSigned={allRequiredSigned}
        fields={detailFields}
        expiresAt={documentData.expiresAt}
      />

      {/* ─── Modals ─────────────────────────────────────── */}

      {/* Decline Modal */}
      <AnimatePresence>
        {showDeclineModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
            onClick={() => setShowDeclineModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-md overflow-hidden rounded-xl bg-white shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border-b border-stone-100 p-6">
                <h3 className="text-lg font-semibold text-stone-900">
                  Decline to Sign?
                </h3>
                <p className="mt-1 text-sm text-stone-500">
                  The sender will be notified of your decision.
                </p>
              </div>

              <div className="p-6">
                <label className="block text-sm font-medium text-stone-700">
                  Reason for declining
                </label>
                <textarea
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                  placeholder="Please explain why you're declining..."
                  rows={3}
                  className="mt-2 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm placeholder-stone-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                />
              </div>

              <div className="flex justify-end gap-3 border-t border-stone-100 p-4">
                <Button
                  variant="outline"
                  onClick={() => setShowDeclineModal(false)}
                  disabled={isSubmitting}
                  className="border-stone-300"
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  className="bg-red-600 hover:bg-red-700"
                  onClick={handleDecline}
                  disabled={!declineReason.trim() || isSubmitting}
                >
                  {isSubmitting ? (
                    <Spinner size="sm" className="mr-2" />
                  ) : (
                    <X className="mr-2 h-4 w-4" />
                  )}
                  Decline
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Signer Acknowledgment Modal */}
      {documentData && showAcknowledgment && !hasAcknowledged && (
        <SignerAcknowledgment
          isOpen={true}
          onConfirm={handleAcknowledge}
          onNotMe={handleNotMe}
          signerInfo={{
            name: getSignerFullName(),
            email: documentData.contact.email,
          }}
          documentInfo={{
            name: ndfDoc.meta.title,
          }}
        />
      )}

      {/* Contact Support Modal */}
      <ContactSupportModal
        isOpen={showContactSupport}
        onClose={handleContactSupportClose}
      />
    </PortalLayout>
  );
}

export default SignNdfDocument;
